import { Box, ButtonExtendedProps, Menu, Stack, Text } from 'grommet';
import { Notification } from 'grommet-icons';
import { ChatMessage } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { notificationLink } from 'src/utils/notificationLink';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useWebSocket from 'react-use-websocket';
import { env } from 'src/config/env';
import { useChatStore, useNotificationStore, useProjectStore, useUserStore } from 'src/context';
import { NotificationIcon } from './NotificationIcon';

export const NotificationsMenu = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  const notificationStore = useNotificationStore();
  const chatStore = useChatStore();
  const navigate = useNavigate();

  const { lastJsonMessage } = useWebSocket(`${env.apiWsHost}:${env.apiPort}`, {
    protocols: ['Authorization', userStore.token],
    shouldReconnect: () => true,
    reconnectInterval: 3000,
    onError: console.error,
    retryOnError: true,
    share: true,
  });

  /** State **/
  const [isLoading, setIsLoading] = useState(true);

  /** Effects **/

  useEffect(() => {
    if (isLoading) notificationStore.list().finally(() => setIsLoading(false));
  }, [isLoading]);

  useEffect(() => {
    if (lastJsonMessage) {
      const msg = notificationStore.handleMessage(lastJsonMessage as ChatMessage);
      if (msg) toast.info(msg.text);
    }
  }, [lastJsonMessage]);

  /** Render **/
  const renderMenuItem = (n: ChatMessage): ButtonExtendedProps => ({
    label: (
      <Box alignSelf="center" direction="row">
        <Text truncate size="14px">
          {n.text}
        </Text>
      </Box>
    ),

    onClick: () => {
      if (!n.source_entity_name) return;
      if (n.source_entity_name === 'chat' && n.source_entity_id) {
        chatStore.openChannel(n.source_entity_id);
        return;
      }
      const link = notificationLink(n, {
        currentProjectName: projectStore.currentProject?.name,
        projects: projectStore.projectList,
      });
      if (link) navigate(link);
    },
    icon: <NotificationIcon notification={n} />,
    size: 'small',
    gap: 'small',
    fill: 'horizontal',
    justify: 'start',
    alignSelf: 'center',
  });

  return (
    <Box pad="small">
      <Menu
        tip="Notifications"
        justifyContent="center"
        dropAlign={{ top: 'bottom', right: 'right' }}
        // Light, like the header's other menus
        dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium', width: { max: '420px' } }}
        icon={
          <Stack anchor="top-right">
            <Box margin={{ vertical: 'small', horizontal: '8px' }}>
              <Notification size="22px" color="white" />
            </Box>
            {notificationStore.unreadCount > 0 && (
              <Box background="alert" pad={{ horizontal: 'xsmall' }} round>
                <Text size="small" color="white">
                  {notificationStore.unreadCount}
                </Text>
              </Box>
            )}
          </Stack>
        }
        items={[
          ...notificationStore.notifications.map(renderMenuItem),
          {
            label: (
              <Box fill="horizontal" align="center">
                <Text size="14px" weight={600} color="brand">
                  See all notifications
                </Text>
              </Box>
            ),
            onClick: () => navigate('/notifications'),
          },
        ]}
        onClick={() => notificationStore.markAsRead()}
      />
    </Box>
  );
});
